import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    getActiveStoreContext,
    parseSlugFromPath,
    readCachedStoreContext,
} from '../../lib/storefront/storeBootstrap.js';
import {
    getPlatformDomain,
    isPlatformHostedHostname,
    parseCustomDomainFromHost,
    parseSubdomainFromHost,
    RESERVED_STORE_SUBDOMAINS,
} from '../../lib/storefront/storefrontHost.js';
import { SUPPORTED_LANGUAGES } from '../../config/languages.js';

function stripPort(host = '') {
    return String(host).trim().toLowerCase().split(':')[0];
}

function needsStorePathPrefix(hostname) {
    const host = stripPort(hostname);
    if (!host) return false;
    if (host === 'localhost' || host === '127.0.0.1') return true;
    return isPlatformHostedHostname(host);
}

/** Brand shared hosts like elvix.quazbit.com — not merchant subdomains. */
function isReservedBrandHostname(hostname, platformDomain) {
    const host = stripPort(hostname);
    const domain = String(platformDomain || '').trim().toLowerCase();
    if (!host || !domain || domain === 'localhost') return false;
    const suffix = `.${domain}`;
    if (!host.endsWith(suffix)) return false;
    const sub = host.slice(0, -suffix.length);
    return Boolean(sub) && !sub.includes('.') && RESERVED_STORE_SUBDOMAINS.has(sub);
}

function stripStorePathPrefix(pathname) {
    const match = String(pathname || '').match(/^\/s\/[^/]+\/[^/]+(.*)$/i);
    if (!match) return null;
    const rest = match[1] || '/';
    return rest.startsWith('/') ? rest : `/${rest}`;
}

/**
 * Keep /s/{org}/{store} only on shared preview hosts (localhost apex, workers.dev).
 * On merchant subdomains / custom domains: never inject, and strip a stale path if present.
 */
export default function StorePathRedirect() {
    const { pathname, search, hash } = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const misplaced = pathname.match(/^\/([a-z]{2})(\/s\/[^/]+\/[^/]+)(?:\/[a-z]{2})?(.*)$/i);
        if (misplaced && SUPPORTED_LANGUAGES.includes(misplaced[1].toLowerCase())) {
            navigate(`${misplaced[2]}/${misplaced[1]}${misplaced[3] || ''}${search}${hash}`, {
                replace: true,
            });
            return;
        }

        const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
        const platformDomain = getPlatformDomain();
        const subdomain = parseSubdomainFromHost(hostname, platformDomain);
        const customDomain = parseCustomDomainFromHost(hostname, platformDomain);
        const ctx = getActiveStoreContext() || readCachedStoreContext();
        const isMerchantHost =
            Boolean(subdomain) ||
            Boolean(customDomain) ||
            ctx?.routingMode === 'subdomain' ||
            ctx?.routingMode === 'custom_domain';

        if (isMerchantHost) {
            const cleaned = stripStorePathPrefix(pathname);
            if (cleaned && cleaned !== pathname) {
                navigate(`${cleaned}${search}${hash}`, { replace: true });
            }
            return;
        }

        if (isReservedBrandHostname(hostname, platformDomain)) return;
        if (!needsStorePathPrefix(hostname)) return;
        if (parseSlugFromPath(pathname)) return;

        const orgSlug = String(ctx?.organization?.slug || process.env.REACT_APP_ORG_SLUG || '').trim();
        const storeSlug = String(ctx?.store?.slug || process.env.REACT_APP_STORE_SLUG || '').trim();
        if (!orgSlug || !storeSlug) return;

        const rest = pathname === '/' ? '' : pathname;
        if (rest.toLowerCase().includes('/s/')) return;

        navigate(`/s/${encodeURIComponent(orgSlug)}/${encodeURIComponent(storeSlug)}${rest}${search}${hash}`, {
            replace: true,
        });
    }, [pathname, search, hash, navigate]);

    return null;
}
